import React, { useEffect, useState } from 'react';
import { createUserRoleMapping, fetchUserRoleMappingByUser } from './UserRoleMappingApi';
import { fetchAllRoles } from '../roles/RoleApi';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faUndo, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

interface UserRoleMappingDTO {
  id?: string;
  userId: string;
  roleId: string;
}

interface Role {
  id: string;
  name: string;
}

interface UserRoleMappingFormProps {
  viewMode?: boolean;
}

const UserRoleMappingForm: React.FC<UserRoleMappingFormProps> = ({ viewMode = false }) => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [roleMappings, setRoleMappings] = useState<UserRoleMappingDTO[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  useEffect(() => {
    fetchAllRoles().then((res) => setRoles(res.data));
    if (userId) {
      fetchUserRoleMappingByUser(userId).then((res) => {
        setRoleMappings(res.data);
        setSelectedRoles(res.data.map((r) => r.roleId));
      });
    }
  }, [userId]);

  const handleRoleChange = (roleId: string, checked: boolean) => {
    if (checked) {
      setSelectedRoles([...selectedRoles, roleId]);
    } else {
      setSelectedRoles(selectedRoles.filter((id) => id !== roleId));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    for (const roleId of selectedRoles) {
      await createUserRoleMapping({ userId, roleId });
    }
    navigate('/admin/users');
  };

  const handleReset = () => {
    setSelectedRoles(roleMappings.map((r) => r.roleId));
  };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white shadow rounded">
      <h2 className="text-xl font-bold mb-4">
        {viewMode ? 'View Assigned Roles' : 'Assign Roles to User'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {roles.map((role) => (
            <label key={role.id} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedRoles.includes(role.id)}
                onChange={(e) => handleRoleChange(role.id, e.target.checked)}
                disabled={viewMode}
              />
              <span>{role.name}</span>
            </label>
          ))}
        </div>

        <div className="flex space-x-3 mt-6">
          {!viewMode && (
            <>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded flex items-center space-x-2"
              >
                <FontAwesomeIcon icon={faSave} />
                <span>Save</span>
              </button>

              <button
                type="button"
                onClick={handleReset}
                className="bg-gray-500 text-white px-4 py-2 rounded flex items-center space-x-2"
              >
                <FontAwesomeIcon icon={faUndo} />
                <span>Reset</span>
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="bg-red-500 text-white px-4 py-2 rounded flex items-center space-x-2"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserRoleMappingForm;